import React, { useState, useEffect } from 'react';
import type { User } from '@supabase/supabase-js';
import type { AppView } from '../types';
import { fetchShowcases, deleteShowcase, duplicateShowcase, type ShowcaseWithHero } from '../services/showcases';
import ShowcaseCard from './ShowcaseCard';
import { Plus, Search, Loader2, LayoutGrid } from 'lucide-react';

interface DashboardProps {
  user: User;
  onNavigate: (view: AppView, id?: string) => void;
}

export default function Dashboard({ user, onNavigate }: DashboardProps) {
  const [showcases, setShowcases] = useState<ShowcaseWithHero[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadShowcases();
  }, []);

  const loadShowcases = async () => {
    try {
      setLoading(true);
      const data = await fetchShowcases(user.id);
      setShowcases(data);
    } catch (err) {
      console.error('Failed to load showcases:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this showcase?')) return;
    try {
      await deleteShowcase(id);
      setShowcases((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const handleDuplicate = async (id: string) => {
    try {
      const newId = await duplicateShowcase(id);
      if (newId) {
        await loadShowcases();
      }
    } catch (err) {
      console.error('Failed to duplicate:', err);
    }
  };

  const filtered = showcases.filter(
    (s) =>
      s.title.toLowerCase().includes(search.toLowerCase()) ||
      s.slug?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Your Showcases</h1>
          <p className="text-slate-400 mt-1">Create beautiful property presentations in minutes</p>
        </div>
        <button
          onClick={() => onNavigate('CREATE')}
          className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Showcase
        </button>
      </div>

      {showcases.length > 0 && (
        <div className="relative mb-6">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search showcases..."
            className="w-full max-w-sm pl-10 pr-4 py-2.5 bg-slate-900 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      ) : showcases.length === 0 ? (
        <div className="text-center py-20 animate-fade-in">
          <div className="w-20 h-20 rounded-2xl bg-slate-800 flex items-center justify-center mx-auto mb-6">
            <LayoutGrid className="w-10 h-10 text-slate-600" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Create your first Showcase</h2>
          <p className="text-slate-400 mb-6 max-w-md mx-auto">
            Take your property presentation to the next level. Turn a listing into an interactive experience with microsites, PDF brochures, and more.
          </p>
          <button
            onClick={() => onNavigate('CREATE')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Create Showcase
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((showcase) => (
            <ShowcaseCard
              key={showcase.id}
              showcase={showcase}
              onEdit={() => onNavigate('EDITOR', showcase.id)}
              onDelete={() => handleDelete(showcase.id)}
              onDuplicate={() => handleDuplicate(showcase.id)}
              onViewLeads={() => onNavigate('LEADS', showcase.id)}
              onViewAnalytics={() => onNavigate('ANALYTICS', showcase.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
